import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useKioskFlexBandData, FlexBandEntry } from './hooks/useKioskFlexBandData';
import { useKioskHeartbeat } from './hooks/useKioskHeartbeat';
import { useKioskTokenRefresh } from './hooks/useKioskTokenRefresh';
import { Loader2, AlertCircle, Calculator, BookOpen, FlaskConical, Palette, Trophy, Music, Globe, Monitor, Languages, HelpCircle, Clock } from 'lucide-react';

// Hook to auto-scale content to fit viewport
const useAutoScale = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    const calculateScale = useCallback(() => {
        if (!containerRef.current || !contentRef.current) return;

        const container = containerRef.current;
        const content = contentRef.current;

        // Get viewport dimensions (minus some padding)
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Reset scale to measure natural size
        content.style.transform = 'scale(1)';
        content.style.transformOrigin = 'top left';

        // Get content's natural dimensions
        const contentWidth = content.scrollWidth;
        const contentHeight = content.scrollHeight;

        // Calculate scale to fit both width and height
        const scaleX = viewportWidth / contentWidth;
        const scaleY = viewportHeight / contentHeight;

        // Use the smaller scale to ensure content fits
        // Remove clamps to allow full fitting on large (4K) or small screens
        const newScale = Math.min(scaleX, scaleY);

        setScale(newScale);
    }, []);

    useEffect(() => {
        // Calculate on mount and window resize
        calculateScale();

        const handleResize = () => {
            calculateScale();
        };

        window.addEventListener('resize', handleResize);

        // Recalculate after content loads (with delay for rendering)
        const timeout = setTimeout(calculateScale, 500);
        const timeout2 = setTimeout(calculateScale, 2000);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeout);
            clearTimeout(timeout2);
        };
    }, [calculateScale]);

    return { containerRef, contentRef, scale, recalculate: calculateScale };
};

interface FlexBandBoardProps {
    kioskInfo?: any;
}

// Helper to group data by Course -> Class -> Students
interface CourseGroup {
    course_name: string;
    subject_icon: string | null;
    teacher_names: string;
    room_name: string;
    room_number?: string;
    is_cancelled: boolean;
    // Substitution indicators
    is_teacher_substituted: boolean;
    is_room_substituted: boolean;
    // For regular courses:
    classes: Record<string, string[]>; // class_name -> [student_names]
    // For cancelled courses:
    unresolved: Record<string, string[]>;
    allein: Record<string, string[]>;
    abholen: Record<string, string[]>;
    gast: Record<string, { name: string; guest_course_name?: string; guest_course_icon?: string }[]>;
}

const processData = (data: FlexBandEntry[]) => {
    const courses: Record<string, CourseGroup> = {};

    data.forEach(entry => {
        const courseName = entry.course_name || 'Sonstiges';

        if (!courses[courseName]) {
            courses[courseName] = {
                course_name: courseName,
                subject_icon: entry.subject_icon,
                teacher_names: entry.teacher_names,
                room_name: entry.room_name,
                room_number: entry.room_number,
                classes: {},
                is_cancelled: entry.is_cancelled,
                is_teacher_substituted: entry.is_teacher_substituted || false,
                is_room_substituted: entry.is_room_substituted || false,
                unresolved: {},
                allein: {},
                abholen: {},
                gast: {}
            };
        }

        const className = entry.class_name || 'Ohne Klasse';
        const group = courses[courseName];

        if (entry.is_cancelled) {
            // Cancelled Course Logic
            const groupName = entry.group_name || '';

            if (groupName.startsWith('Gast in')) {
                if (!group.gast[className]) group.gast[className] = [];
                group.gast[className].push({
                    name: entry.student_first_name,
                    guest_course_name: entry.guest_course_name,
                    guest_course_icon: entry.guest_course_icon
                });
            } else if (groupName.startsWith('allein')) {
                if (!group.allein[className]) group.allein[className] = [];
                group.allein[className].push(entry.student_first_name);
            } else if (groupName.startsWith('abholen')) {
                if (!group.abholen[className]) group.abholen[className] = [];
                group.abholen[className].push(entry.student_first_name);
            } else {
                // "unresolved" or just raw list (Normal participants)
                if (!group.unresolved[className]) group.unresolved[className] = [];
                group.unresolved[className].push(entry.student_first_name);
            }

        } else {
            // Regular Course Logic
            let renderClassName = className;
            if (entry.is_guest) {
                renderClassName = 'Gastkinder';
            }
            if (!group.classes[renderClassName]) {
                group.classes[renderClassName] = [];
            }
            group.classes[renderClassName].push(entry.student_first_name);
        }
    });

    return Object.values(courses).sort((a, b) => {
        if (a.is_cancelled && !b.is_cancelled) return 1;
        if (!a.is_cancelled && b.is_cancelled) return -1;
        return a.course_name.localeCompare(b.course_name);
    });
};

// Theme configuration matching the user's image style
const THEMES = [
    { name: 'purple', header: 'bg-purple-200 text-purple-900', body: 'bg-purple-50 text-purple-900', border: 'border-purple-200' },
    { name: 'cyan', header: 'bg-cyan-200 text-cyan-900', body: 'bg-cyan-50 text-cyan-900', border: 'border-cyan-200' },
    { name: 'green', header: 'bg-emerald-200 text-emerald-900', body: 'bg-emerald-50 text-emerald-900', border: 'border-emerald-200' },
    { name: 'pink', header: 'bg-pink-200 text-pink-900', body: 'bg-pink-50 text-pink-900', border: 'border-pink-200' },
    { name: 'orange', header: 'bg-orange-200 text-orange-900', body: 'bg-orange-50 text-orange-900', border: 'border-orange-200' },
    { name: 'yellow', header: 'bg-amber-200 text-amber-900', body: 'bg-amber-50 text-amber-900', border: 'border-amber-200' },
    { name: 'blue', header: 'bg-blue-200 text-blue-900', body: 'bg-blue-50 text-blue-900', border: 'border-blue-200' },
];

const getTheme = (index: number) => THEMES[index % THEMES.length];

// Helper to extract emoji from string
const extractEmoji = (text: string) => {
    // Matches most common emojis
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u;
    const match = text.match(emojiRegex);
    return match ? match[0] : null;
};

// Icon mapping based on keywords (fallback)
const getFallbackIcon = (courseName: string) => {
    const lower = courseName.toLowerCase();
    if (lower.includes('judo')) return <span className="text-2xl leading-none filter drop-shadow-sm">🥋</span>;
    if (lower.includes('mathe')) return <Calculator className="w-6 h-6" />;
    if (lower.includes('deutsch') || lower.includes('lesen') || lower.includes('literatur')) return <BookOpen className="w-6 h-6" />;
    if (lower.includes('bio') || lower.includes('chemie') || lower.includes('physik') || lower.includes('forschen')) return <FlaskConical className="w-6 h-6" />;
    if (lower.includes('kunst') || lower.includes('töpfern') || lower.includes('malen') || lower.includes('basteln')) return <Palette className="w-6 h-6" />;
    if (lower.includes('sport') || lower.includes('fußball') || lower.includes('turnen') || lower.includes('beweg')) return <Trophy className="w-6 h-6" />;
    if (lower.includes('musik') || lower.includes('chor') || lower.includes('orchester') || lower.includes('singen')) return <Music className="w-6 h-6" />;
    if (lower.includes('geo') || lower.includes('erdkunde')) return <Globe className="w-6 h-6" />;
    if (lower.includes('info') || lower.includes('computer') || lower.includes('medien')) return <Monitor className="w-6 h-6" />;
    if (lower.includes('englisch') || lower.includes('französisch') || lower.includes('spanisch')) return <Languages className="w-6 h-6" />;
    return null;
};

export default function FlexBandBoard({ kioskInfo }: FlexBandBoardProps) {
    const { data, loading, error, lastUpdated } = useKioskFlexBandData();
    const [currentTime, setCurrentTime] = useState(new Date());
    const { containerRef, contentRef, scale, recalculate } = useAutoScale();

    // Send heartbeat every 30 seconds to update monitor status
    useKioskHeartbeat(true, 30000);

    // Auto-refresh JWT token before expiration (checks hourly, refreshes when < 24h remaining)
    useKioskTokenRefresh(true);

    // Auto-dismiss cookie banners and deny notification permissions for kiosk mode
    useEffect(() => {
        // Deny notification permission requests silently
        if ('Notification' in window && Notification.permission === 'default') {
            // Override the requestPermission to auto-deny
            const originalRequestPermission = Notification.requestPermission.bind(Notification);
            Notification.requestPermission = () => {
                return Promise.resolve('denied' as NotificationPermission);
            };
        }

        // Auto-click cookie consent buttons
        const autoDismissCookies = () => {
            const cookieSelectors = [
                '[class*="cookie"] button[class*="accept"]',
                '[class*="cookie"] button[class*="verstanden"]',
                '[class*="consent"] button[class*="accept"]',
                '[class*="consent"] button[class*="agree"]',
                'button[id*="cookie-accept"]',
                'button[class*="cc-accept"]',
                'button[class*="cc-dismiss"]',
                '#CybotCookiebotDialogBodyLevelButtonLevelOptinAllowAll',
                '.cookie-consent button',
                'button:has-text("Verstanden")',
                'button:has-text("Akzeptieren")',
                'button:has-text("Accept")',
            ];

            cookieSelectors.forEach(selector => {
                try {
                    const buttons = document.querySelectorAll(selector);
                    buttons.forEach(btn => {
                        if (btn instanceof HTMLElement) {
                            btn.click();
                        }
                    });
                } catch (e) {
                    // Ignore invalid selectors
                }
            });

            // Also try finding buttons by text content
            document.querySelectorAll('button').forEach(btn => {
                const text = btn.textContent?.toLowerCase() || '';
                if (text.includes('verstanden') || text.includes('akzeptieren') || text.includes('accept') || text.includes('agree')) {
                    if (btn.closest('[class*="cookie"]') || btn.closest('[class*="consent"]')) {
                        btn.click();
                    }
                }
            });
        };

        // Run immediately and after short delays to catch late-loading banners
        autoDismissCookies();
        const t1 = setTimeout(autoDismissCookies, 1000);
        const t2 = setTimeout(autoDismissCookies, 3000);
        const t3 = setTimeout(autoDismissCookies, 5000);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, []);

    // Recalculate scale when data changes
    useEffect(() => {
        if (data.length > 0) {
            // Give time for DOM to update
            const timeout = setTimeout(recalculate, 100);
            return () => clearTimeout(timeout);
        }
    }, [data, recalculate]);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const courseGroups = useMemo(() => processData(data), [data]);

    // Static background icons (no animation for performance)
    const backgroundIcons = useMemo(() => {
        return [...Array(15)].map((_, i) => {
            const Icon = [BookOpen, Calculator, Palette, Trophy, Music, Globe, FlaskConical, Monitor][i % 8];
            return {
                Icon,
                key: i,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                size: Math.random() * 40 + 40, // 40px to 80px
            };
        });
    }, []);

    if (loading && data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-white">
                <Loader2 className="w-16 h-16 animate-spin text-slate-300 mb-4" />
                <p className="text-xl font-medium text-slate-400">Lade Kursübersicht...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-white p-8 text-center">
                <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Verbindungsfehler</h2>
                <p className="text-slate-500">{error}</p>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="min-h-screen bg-white text-slate-900 font-sans relative overflow-hidden">
            <div
                ref={contentRef}
                className="p-4 md:p-8 origin-top-left"
                style={{
                    transform: `scale(${scale})`,
                    width: scale !== 1 ? `${100 / scale}%` : '100%',
                    minHeight: scale !== 1 ? `${100 / scale}vh` : '100vh'
                }}
            >
                <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-up {
                    animation: fadeInUp 0.6s ease-out forwards;
                    opacity: 0;
                }
                /* Background floating icons removed for performance */
                /* Hide Sentry bug report button on kiosk display */
                #sentry-feedback,
                #sentry-feedback-button,
                .sentry-feedback-widget,
                .sentry-feedback-button,
                [id^="sentry-feedback"],
                [class*="sentry-feedback"],
                [aria-label="Report a Bug"],
                [aria-label="Bug melden"] {
                    display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
                z-index: -9999 !important;
                }
                /* Hide cookie consent banners */
                [class*="cookie"],
                [id*="cookie"],
                [class*="consent"],
                [id*="consent"],
                [class*="CookieConsent"],
                [class*="cookie-banner"],
                [class*="cookie-notice"],
                [class*="gdpr"],
                .cookie-consent,
                .cookie-banner,
                .cc-window,
                .cc-banner,
                #cookie-notice,
                #cookie-law-info-bar,
                #CybotCookiebotDialog,
                /* Hide notification permission prompts (browser UI can't be hidden, but we hide any custom ones) */
                [class*="notification-prompt"],
                [class*="push-prompt"],
                [class*="NotificationPrompt"],
                [role="alertdialog"][class*="notification"] {
                    display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                pointer-events: none !important;
                }
            `}</style>

                {/* Static Background Icons (no animation) */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 opacity-20">
                    {backgroundIcons.map((icon) => (
                        <div
                            key={icon.key}
                            className="absolute text-slate-900"
                            style={{
                                top: icon.top,
                                left: icon.left,
                                width: icon.size,
                                height: icon.size,
                            }}
                        >
                            <icon.Icon className="w-full h-full" />
                        </div>
                    ))}
                </div>

                <div className="relative z-10">
                    {/* Header */}
                    <header className="text-center mb-12">
                        <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-2 tracking-tight drop-shadow-sm">
                            FlexBand – {currentTime.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </h1>
                    </header>

                    {/* Grid */}
                    {courseGroups.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-3xl font-bold text-slate-300">Heute finden keine Kurse statt.</p>
                        </div>
                    ) : (
                        <div className="columns-1 md:columns-3 xl:columns-4 2xl:columns-5 gap-6">
                            {courseGroups.map((group, index) => {
                                const theme = getTheme(index);

                                // Determine icon and display name
                                let icon = null;
                                let displayName = group.course_name;

                                if (group.subject_icon) {
                                    // Use icon from database (subjects table)
                                    icon = <span className="text-3xl leading-none filter drop-shadow-md transform transition-transform duration-500 hover:scale-110">{group.subject_icon}</span>;
                                    // If the icon is also in the name, remove it
                                    displayName = displayName.replace(group.subject_icon, '').trim();
                                } else {
                                    // Fallback: extract emoji from name
                                    const emoji = extractEmoji(group.course_name);
                                    if (emoji) {
                                        icon = <span className="text-3xl leading-none filter drop-shadow-md transform transition-transform duration-500 hover:scale-110">{emoji}</span>;
                                        displayName = displayName.replace(emoji, '').trim();
                                    } else {
                                        // Fallback: Lucide icon
                                        icon = getFallbackIcon(group.course_name);
                                    }
                                }

                                return (
                                    <div
                                        key={group.course_name}
                                        className={`break-inside-avoid mb-6 rounded-xl overflow-hidden border-2 ${theme.border} shadow-lg flex flex-col animate-fade-in-up hover:shadow-xl transition-shadow duration-300 ${group.is_cancelled ? 'opacity-90' : ''}`}
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        {/* Card Header */}
                                        <div className={`${theme.header} px-4 py-3 flex flex-col items-center justify-center gap-1 border-b-2 ${theme.border} relative overflow-hidden`}>
                                            {group.is_cancelled && (
                                                <div className="absolute top-2 right-2 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded shadow-md transform rotate-12 z-50">
                                                    FÄLLT AUS
                                                </div>
                                            )}
                                            <div className="flex items-center justify-center gap-2 mb-1">
                                                {icon && (
                                                    <div className={group.is_cancelled ? 'opacity-50 grayscale' : ''}>
                                                        {icon}
                                                    </div>
                                                )}
                                                <h2 className={`text-xl font-black tracking-tight text-center leading-tight ${group.is_cancelled ? 'line-through decoration-red-500 decoration-4 opacity-70' : ''}`}>
                                                    {displayName}
                                                </h2>
                                            </div>
                                            <div className="text-sm font-bold opacity-90 flex items-center justify-center gap-1 flex-wrap">
                                                <span className={group.is_teacher_substituted ? 'inline-flex items-center gap-1' : ''}>
                                                    {group.teacher_names}
                                                    {group.is_teacher_substituted && <span className="text-base" title="Vertretung">🔄</span>}
                                                </span>
                                                {group.room_number && (
                                                    <span className={`ml-1 font-normal ${group.is_room_substituted ? 'text-red-600 font-semibold' : 'opacity-75'}`}>
                                                        ({group.room_number})
                                                        {group.is_room_substituted && <span className="ml-0.5 text-base" title="Raumänderung">🔄</span>}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div className={`${theme.body} p-4 flex-1 flex flex-col text-sm`}>

                                            {!group.is_cancelled ? (
                                                // REGULAR COURSE DISPLAY
                                                <div className="w-full text-center flex flex-wrap justify-center gap-x-3 gap-y-1">
                                                    {Object.entries(group.classes).sort((a, b) => {
                                                        const aKey = a[0];
                                                        const bKey = b[0];
                                                        if (aKey === 'Gastkinder') return 1;
                                                        if (bKey === 'Gastkinder') return -1;
                                                        return aKey.localeCompare(bKey);
                                                    }).map(([className, students]) => (
                                                        <span key={className} className={`leading-relaxed ${className === 'Gastkinder' ? 'text-blue-600 font-semibold' : ''}`}>
                                                            {className && (
                                                                <span className="font-bold mr-1">
                                                                    {className === 'Gastkinder' ? 'Gastkinder:' : className}
                                                                </span>
                                                            )}
                                                            <span className="opacity-90">{students.sort().join(', ')}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                // CANCELLED COURSE DISPLAY
                                                <div className="flex flex-col gap-1">

                                                    {/* 1. Unresolved - Bleiben bis 16:00 (Treffpunkt Mensa) */}
                                                    {Object.keys(group.unresolved).length > 0 && (
                                                        <div className="leading-relaxed text-center text-xs">
                                                            <span className="font-bold text-slate-700">Bleiben bis 16:00 (Treffpunkt Mensa):</span>{' '}
                                                            {Object.entries(group.unresolved).sort((a, b) => a[0].localeCompare(b[0])).map(([className, students], i) => (
                                                                <span key={className} className="inline mr-2">
                                                                    <span className="font-bold">{className}</span> <span className="text-slate-600">{students.sort().join(', ')}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 2. Gast im Modul - Bleiben bis 16:00 (Gast im Modul) */}
                                                    {Object.keys(group.gast).length > 0 && (
                                                        <div className="text-center text-xs border-t border-blue-200 pt-1 mt-1">
                                                            <span className="font-bold text-blue-700">Bleiben bis 16:00 (Gast im Modul):</span>{' '}
                                                            {Object.entries(group.gast).sort((a, b) => a[0].localeCompare(b[0])).map(([className, students]) => (
                                                                <span key={className} className="inline mr-2 text-blue-800">
                                                                    <span className="font-bold">{className}</span>{' '}
                                                                    {students.sort((a, b) => a.name.localeCompare(b.name)).map((s, idx) => (

                                                                        <span key={s.name}>
                                                                            {idx > 0 && ', '}
                                                                            {s.name}
                                                                            {s.guest_course_icon && <span className="ml-[1px] opacity-75 text-[0.9em]">{extractEmoji(s.guest_course_icon) || ''}</span>}
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 3. Abholen */}
                                                    {Object.keys(group.abholen).length > 0 && (
                                                        <div className="text-center text-xs border-t border-orange-200 pt-1 mt-1">
                                                            <span className="font-bold text-orange-700">Abholen 14:30:</span>{' '}
                                                            {Object.entries(group.abholen).sort((a, b) => a[0].localeCompare(b[0])).map(([className, students]) => (
                                                                <span key={className} className="inline mr-2 text-orange-800">
                                                                    <span className="font-bold">{className}</span> {students.sort().join(', ')}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* 4. Allein */}
                                                    {Object.keys(group.allein).length > 0 && (
                                                        <div className="text-center text-xs border-t border-orange-200 pt-1 mt-1">
                                                            <span className="font-bold text-orange-700">Allein 14:30:</span>{' '}
                                                            {Object.entries(group.allein).sort((a, b) => a[0].localeCompare(b[0])).map(([className, students]) => (
                                                                <span key={className} className="inline mr-2 text-orange-800">
                                                                    <span className="font-bold">{className}</span> {students.sort().join(', ')}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}

                                                </div>
                                            )}

                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="fixed bottom-0 left-0 right-0 bg-emerald-50 border-t border-emerald-100 p-3 text-center text-emerald-800 text-sm font-medium backdrop-blur-sm bg-opacity-90">
                        <div className="flex items-center justify-center gap-2">
                            <span>Letzte Aktualisierung: {lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    </div>
                </div>
            </div>{/* Close scaled content div */}
        </div >
    );
}
